import { AbstractRepository } from '@/repositories/abstract-repository';
import { PendingFamily, RejectFamilyInterface } from '@/families/models/family';
import { PromiseStore } from '@/store/promise-store';
import { getModule } from 'vuex-module-decorators';

const promisesStore = getModule(PromiseStore);

export class PendingLeadsRepository extends AbstractRepository<PendingFamily> {
    readonly endpoint = 'families/pending';

    /**
     * Accept a pending family.
     *
     * @param familyId The ID of the family to accept.
     * @param centerId The ID of the center to associate the family with.
     * @param doNotRunWorkflows If true, adds the 'do_not_run_workflow' query param to prevent workflows from running.
     */
    public async acceptFamily<CrmEntity>(familyId: number, centerId: number | null = null, doNotRunWorkflows = false): Promise<void> {
        let data: { centerId?: number; doNotRunWorkflows?: boolean } | undefined;

        // Only create the data object if there is something to put in it.
        if (centerId !== null || doNotRunWorkflows) {
            data = {}; // Create object on demand
            if (centerId !== null) {
                data.centerId = centerId;
            }
            if (doNotRunWorkflows) {
                data.doNotRunWorkflows = doNotRunWorkflows;
            }
        }
        await promisesStore.initPromise({
            hash: this.endpoint + '/' + familyId.toString() + '/accept',
            closure: async () => {
                return this.client.put(this.endpoint + '/' + familyId.toString() + '/accept', data);
            },
            force: false,
            ttl: 5
        });
    }

    /**
     * Bulk accept pending families.
     *
     * @param pendingIds
     */
    public async bulkAccept(pendingIds: Array<number>): Promise<void> {
        await promisesStore.initPromise({
            hash: `${this.endpoint}/accept`,
            closure: async () => {
                return this.client.put(`${this.endpoint}/accept`, {
                    ids: pendingIds
                });
            },
            force: false,
            ttl: 5
        });
    }

    /**
     * Bulk reject pending families.
     *
     * @param pendingIds
     */
    public async bulkReject(pendingIds: Array<number>): Promise<void> {
        await promisesStore.initPromise({
            hash: `${this.endpoint}/reject`,
            closure: async () => {
                return this.client.put(`${this.endpoint}/reject`, {
                    ids: pendingIds
                });
            },
            force: false,
            ttl: 5
        });
    }

    /**
     * Reject a pending family.
     *
     * @param familyId
     * @param existingFamilyId
     * @param reasonId The rejected reason id
     */
    public async rejectFamily<CrmEntity>(
        familyId: number,
        existingFamilyId: number | null = null,
        reasonId: number | null = null
    ): Promise<void> {
        const rejectedPayload = {
            merge_to_family_id: existingFamilyId,
            reason: reasonId
        } as RejectFamilyInterface;

        await promisesStore.initPromise({
            hash: this.endpoint + '/' + familyId.toString() + '/reject',
            closure: async () => {
                return this.client.put(this.endpoint + '/' + familyId.toString() + '/reject', rejectedPayload);
            },
            force: false,
            ttl: 5
        });
    }
}
